import React from 'react'

import { Color, Link, LinkProps, LinkVariant, withCitrusTheme } from '@gousto-internal/citrus-react'

export type CWCLinkButtonVariant = 'primary' | 'secondary'

export type CWCLinkButtonProps = {
  href: string
  children: React.ReactNode
  onClick?: () => void
  /**
   * `primary` (default) = solid purple fill (CWCHero CTA). `secondary` = transparent fill
   * with a 2px purple outline and purple text (CwC Suppliers CTA).
   */
  variant?: CWCLinkButtonVariant
}

/**
 * A navigational CTA that looks like a Citrus button but is a real anchor (`<a href>`)
 * under the hood — Citrus has no link-styled-as-button primitive, and elements that
 * navigate to another page should be links, not buttons.
 *
 * Built on the Citrus `Link`, so its typography is inherited; we only override the button
 * appearance (fill/outline, radius, padding, text colour, hover/focus). Both variants use
 * the `Secondary_400` token (#615CFF) and an 8px radius. Full-width on mobile, a wider fixed
 * width from tablet up, matching the design.
 *
 * The visual variant travels as `buttonVariant`, stripped before it reaches the DOM (mirrors
 * `InlineLink`), so it never collides with Citrus `Link`'s own `variant` prop.
 */
type StyledLinkProps = LinkProps & { buttonVariant?: CWCLinkButtonVariant }

const StyledLink = withCitrusTheme<StyledLinkProps>(
  (props: StyledLinkProps) => {
    const { buttonVariant, ...linkProps } = props

    return <Link {...linkProps} />
  },
  (theme, props: StyledLinkProps) => {
    const isSecondary = props.buttonVariant === 'secondary'

    return [
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '48px',
        padding: '10px 24px',
        borderRadius: '8px',
        fontFamily: theme.tokens.FontFamilies.bold,
        textDecoration: 'none',
        // Secondary (Suppliers CTA) is 16px per ui-design; primary keeps the inherited Link size.
        fontSize: isSecondary ? '16px' : undefined,
        backgroundColor: isSecondary ? 'transparent' : theme.color(Color.Secondary_400),
        color: isSecondary ? theme.color(Color.Secondary_400) : theme.color(Color.White),
        border: isSecondary ? `2px solid ${theme.color(Color.Secondary_400)}` : 'none',
        ':hover': {
          backgroundColor: isSecondary
            ? theme.color(Color.Secondary_50)
            : theme.color(Color.Secondary_600),
          color: isSecondary ? theme.color(Color.Secondary_600) : theme.color(Color.White),
        },
        ':focus-visible': {
          outline: `1px solid ${theme.color(Color.Secondary_600)}`,
          outlineOffset: '2px',
        },
      },
      theme.responsiveStyle(['100%', null, '345px'], ['width']),
    ]
  },
)

export const CWCLinkButton = ({
  href,
  children,
  onClick,
  variant = 'primary',
}: CWCLinkButtonProps) => (
  <StyledLink href={href} variant={LinkVariant.None} buttonVariant={variant} onClick={onClick}>
    {children}
  </StyledLink>
)
